import React, { useContext, useState } from 'react'
import { useLazyQuery, useMutation } from 'react-apollo'

import { SelectInputV3, Spinner, TextInputV3 } from '@acorns/web-components'
import { useApolloErrorModal } from '@acorns/web-utils'
import { useFormik } from 'formik'
import { object, string } from 'yup'

import AutocompleteAddress from 'components/AutocompleteAddress'
import { OnAutoCompleteSelect } from 'components/AutocompleteAddress/types'
import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Separator from 'components/Separator'
import { getGeneralError } from 'utils/getGeneralError'
import { STATE_ABBRS } from 'utils/states'

import { emptyAddress } from '../constants'
import VerifyDeliveryAddress from '../gql/address-validation-query.gql'
import UpdateShippingInfo from '../gql/update-shipping-address.gql'
import { HeroHeader, HeroText } from '../shared-components'
import { AddressType, ShippingAddressContext } from '../ShippingAddressContext'
import { ShippingSubRoutes } from '../types'
import { pickAddress } from '../utils'
import {
  addressDeliverabilityUnavailable,
  addressVerificationError,
  addressVerified,
  correctedAddress,
} from '../validation-helpers'
import { useEditAddressTracking } from './events'
import {
  AddressRow,
  CityStateZipRow,
  StyledButton,
  StyledForm,
  TextInputWrapper,
} from './styled-components'

const addressError = 'Please enter a valid address'
const cityError = 'Please enter a valid city'
const stateError = 'Please select a state'
const zipCodeError = 'Please enter a valid zip code'

const schema = object().shape({
  street1: string()
    .min(5, addressError)
    .required(addressError),
  street2: string().ensure(),
  city: string().required(cityError),
  state: string().required(stateError),
  zipCode: string()
    .min(5, zipCodeError)
    .max(5, zipCodeError)
    .matches(/\d$/, zipCodeError)
    .required(zipCodeError),
})

export const EditAddress = () => {
  const track = useEditAddressTracking()
  const { addresses, setAddress, show } = useContext(ShippingAddressContext)
  const currentShippingAddress = pickAddress(addresses.residential, addresses.mailing)
  const [submitting, setSubmitting] = useState(false)
  const [updateShippingAddress] = useMutation(UpdateShippingInfo)
  const { closeErrorModal, showErrorModal } = useApolloErrorModal()
  const ctaTitle = 'Next'

  const onSubmit = () => {
    track.ctaTapped(ctaTitle)
    setSubmitting(true)
    verifyAddress()
  }
  const onChange = (e: React.ChangeEvent<unknown>) => {
    formik.handleChange(e)
    formik.setErrors({})
  }

  const formik = useFormik({
    initialValues: {
      ...currentShippingAddress,
    },
    onSubmit: onSubmit,
    validationSchema: schema,
    validateOnChange: false,
    enableReinitialize: true,
  })

  const onStateChange = (field: string, value: string) => {
    formik.setFieldValue(field, value)
  }

  const onAutocompleteSelect: OnAutoCompleteSelect = googleAddress => {
    formik.setFieldValue('street1', googleAddress.street1)
    formik.setFieldValue('street2', googleAddress.street2)
    formik.setFieldValue('city', googleAddress.city)
    formik.setFieldValue('state', googleAddress.stateAbbreviation)
    formik.setFieldValue('zipCode', googleAddress.zipCode)
  }

  const [verifyAddress, { data: verifyAddressData }] = useLazyQuery(VerifyDeliveryAddress, {
    fetchPolicy: 'network-only',
    variables: {
      street1: formik.values.street1,
      street2: formik.values.street2,
      city: formik.values.city,
      state: formik.values.state,
      zipCode: formik.values.zipCode,
      country: 'US',
    },
    onCompleted: () => {
      const verifyAddressResult = verifyAddressData.addressDeliverability
      if (addressVerified(verifyAddressResult)) {
        updateShippingAddress({
          variables: {
            street1: formik.values.street1,
            street2: formik.values.street2,
            city: formik.values.city,
            state: formik.values.state,
            zipCode: formik.values.zipCode,
          },
        }).then(() => {
          setAddress(AddressType.mailing, formik.values)
          show(ShippingSubRoutes.Preview)
        })
      } else if (correctedAddress(verifyAddressResult)) {
        setAddress(AddressType.unverified, formik.values)
        setAddress(AddressType.corrected, {
          ...emptyAddress,
          ...verifyAddressResult,
        })
        show(ShippingSubRoutes.Validation)
      } else if (addressVerificationError(verifyAddressResult)) {
        setAddress(AddressType.unverified, formik.values)
        show(ShippingSubRoutes.Error)
      } else if (addressDeliverabilityUnavailable(verifyAddressResult)) {
        const { title, message, actions, image } = getGeneralError(
          'Service unavailable',
          { closeErrorModal },
          () => {
            closeErrorModal()
            onSubmit()
          },
          500,
        )
        showErrorModal({ title, message, actions, image })
        setSubmitting(false)
      }
    },
    onError: _error => {
      setSubmitting(false)
    },
  })

  return (
    <Layout header="header" text="text">
      <HeroHeader>Update your shipping address</HeroHeader>
      <HeroText>
        Be sure to double-check for spelling mistakes and include an apartment number if applicable.
      </HeroText>
      <StyledForm autoComplete="off" method="post" name="Delivery Address">
        <AddressRow>
          <TextInputWrapper>
            <AutocompleteAddress
              {...formik}
              name={'street1'}
              id={'street2'}
              type={'text'}
              placeholder={'123 Oak Drive'}
              value={formik.values.street1}
              onChange={onChange}
              onAutoCompleteSelect={onAutocompleteSelect}
            />
          </TextInputWrapper>
          <TextInputWrapper>
            <TextInputV3
              label={{ value: 'Address Line 2' }}
              inputProps={{
                name: 'street2',
                value: formik.values.street2,
                onChange: onChange,
              }}
            />
          </TextInputWrapper>
        </AddressRow>
        <CityStateZipRow>
          <TextInputWrapper>
            <TextInputV3
              label={{ value: 'City *' }}
              inputProps={{
                id: 'city',
                name: 'city',
                value: formik.values.city,
                onChange: onChange,
              }}
              error={!!formik.errors.city}
              errorText={formik.errors.city}
            />
          </TextInputWrapper>
          <SelectInputV3
            id="state"
            label="State *"
            name="state"
            onChange={onStateChange}
            error={!!formik.errors.state}
            errorText={formik.errors.state}
            options={[...STATE_ABBRS]}
            value={formik.values.state}
          />
          <TextInputWrapper>
            <TextInputV3
              label={{ value: 'Zip code *' }}
              inputProps={{
                name: 'zipCode',
                value: formik.values.zipCode,
                onChange: onChange,
                maxLength: 5,
              }}
              error={!!formik.errors.zipCode}
              errorText={formik.errors.zipCode}
            />
          </TextInputWrapper>
        </CityStateZipRow>
      </StyledForm>
      <Separator />
      <CTAContainer>
        <StyledButton
          buttonType={'submit'}
          onPress={formik.handleSubmit}
          disabled={!formik.isValid || submitting}
        >
          {submitting ? <Spinner height={20} width={20} white /> : ctaTitle}
        </StyledButton>
      </CTAContainer>
    </Layout>
  )
}
